'use client'

import { useEffect, useState } from 'react'
import { dispatch, useSelector } from '@/lib/store'
import { updatePackageData } from '@/lib/reducers/packageReducer'
import { consumerRequest } from '@/helper/request'
import {
  POPULAR_HEALTH_CHECKUPS_API,
  SIGNED_IN_PRODUCT_SEARCH_API,
  SIGNED_OUT_PRODUCT_SEARCH_API,
} from '@/helper/apiConstants'
import { CURRENT_SUBSIDIARY, HOME_PAGE } from '@/utils/constants'
import useDebounce from '@/hooks/use-debounce'
import useCustomToast from '@/hooks/useCustomToast'
import { usePathname } from 'next/navigation'

// Shared search + popular-data logic for the test/package search UIs
// (SearchTestsAndPackagesModal dialog and HeaderSearchBar popover).
// Three components trigger this - SecondaryNav on mount, the header popover and the search
// modal on open - so two can overlap before either sets the store. Keyed by city because the
// popular list differs per city; concurrent callers share one request instead of racing.
let inFlightPopular: { cityKey: string; promise: Promise<any> } | null = null

// A city once attempted is never retried this session, even if the response was empty or
// errored - otherwise `packageState.isLoading` flipping back to false on a non-populating
// response re-satisfies every guard below and the fetch fires again forever.
const attemptedPopularCityKeys = new Set<string>()

interface PopularAndSearchOptions {
  // Set when the consumer is actually showing popular data, so mobile visitors who never open search pay nothing.
  loadPopular?: boolean
}

const usePopularHealthCheckupsAndSearchProducts = ({
  loadPopular = false,
}: PopularAndSearchOptions = {}) => {
  const showToast = useCustomToast()
  const pathname = usePathname()

  const cityState = useSelector((state) => state.city) || {}
  const loginState = useSelector((state) => state.login) || {}
  const packageState = useSelector((state) => state.package) || {}

  const mostPopularPackage = packageState?.mostPopularPackage || []
  const mostPopularTest = packageState?.mostPopularTest || []

  const [searchQuery, setSearchQuery] = useState('')
  const [searchQueryProductData, setSearchQueryProductData] = useState<any[]>(
    []
  )
  const [loading, setLoading] = useState(false)
  const [showFlag, setShowFlag] = useState(false)

  const debounceSearch = useDebounce(searchQuery, 500)

  // Dispatches only its own keys: spreading the stale packageState closure would clobber whatever landed meanwhile.
  const fetchMostPopularHealthCheckups = async () => {
    setLoading(true)
    dispatch(updatePackageData({ isLoading: true }))
    const formData = {
      subsidiary: CURRENT_SUBSIDIARY,
      cityUuid: cityState?.currentCityObj?.uuid,
      citySlugName: cityState?.currentCityObj?.slugName,
    }
    const cityKey = `${formData.cityUuid ?? ''}:${formData.citySlugName ?? ''}`
    if (inFlightPopular?.cityKey !== cityKey) {
      inFlightPopular = {
        cityKey,
        promise: consumerRequest(POPULAR_HEALTH_CHECKUPS_API, 'post', formData),
      }
    }

    let apiRes: any
    try {
      apiRes = await inFlightPopular.promise
    } finally {
      if (inFlightPopular?.cityKey === cityKey) inFlightPopular = null
    }
    if (apiRes?.status) {
      dispatch(
        updatePackageData({
          mostPopularTest: apiRes?.data?.MOST_POPULAR_TEST || [],
          mostPopularPackage: apiRes?.data?.MOST_POPULAR_PACKAGE || [],
          isLoading: false,
        })
      )
    } else {
      dispatch(updatePackageData({ isLoading: false }))
    }
    setLoading(false)
  }

  const fetchSearchedTestsAndPackages = async () => {
    if (debounceSearch.length < 3) {
      setSearchQueryProductData([])
      return
    }
    if (!searchQuery || !cityState?.currentCityObj?.uuid) {
      showToast('danger', 'Please input any test or packages')
      return
    }
    const formData = {
      cityUuid: cityState?.currentCityObj?.uuid,
      searchText: searchQuery,
    }
    setLoading(true)
    const FINAL_API_PATH = loginState.isUserLoggedIn
      ? SIGNED_IN_PRODUCT_SEARCH_API
      : SIGNED_OUT_PRODUCT_SEARCH_API
    const apiResponse: any = await consumerRequest(
      FINAL_API_PATH,
      'post',
      formData
    )
    if (apiResponse?.status === true) {
      setSearchQueryProductData(apiResponse?.data)
    } else {
      setSearchQueryProductData([])
    }
    setLoading(false)
  }

  useEffect(() => {
    if (!debounceSearch) return
    fetchSearchedTestsAndPackages()
  }, [debounceSearch])

  useEffect(() => {
    if (!searchQuery) {
      setSearchQueryProductData([])
    }
  }, [searchQuery])

  const hasPopularData =
    mostPopularPackage?.length > 0 || mostPopularTest?.length > 0
  const isHomePage = pathname === HOME_PAGE

  // The only trigger in the app: three components used to repeat these guards and could drift apart.
  useEffect(() => {
    if (!loadPopular || isHomePage || hasPopularData) return
    const cityUuid = cityState?.currentCityObj?.uuid
    if (!cityUuid || attemptedPopularCityKeys.has(cityUuid)) return
    attemptedPopularCityKeys.add(cityUuid)
    fetchMostPopularHealthCheckups()
  }, [loadPopular, isHomePage, hasPopularData, cityState?.currentCityObj?.uuid])

  return {
    pathname,
    cityState,
    loginState,
    packageState,
    mostPopularPackage,
    mostPopularTest,
    searchQuery,
    setSearchQuery,
    searchQueryProductData,
    setSearchQueryProductData,
    loading,
    showFlag,
    setShowFlag,
    debounceSearch,
    hasPopularData,
    isHomePage,
    fetchSearchedTestsAndPackages,
  }
}

export default usePopularHealthCheckupsAndSearchProducts
